import React from "react";
import { Helmet } from "react-helmet-async";
import { Box, Typography, Divider } from "@mui/material";
import { useNavigate } from "react-router-dom";
import SectionNavbar from "../../components/SectionNavbar/SectionNavbar";
import MiniFooter from "../../components/MiniFooter/MiniFooter";

const PAGES = [
  {
    label: "ETF Screener",
    desc: "Filter and explore all ETFs",
    path: "/screener",
  },
  {
    label: "ETF Research",
    desc: "Deep-dive into individual ETFs",
    path: "/funds/etf/SBI-ETF-Nifty-50",
  },
  {
    label: "Indices Research",
    desc: "Track and analyse indices",
    path: "/indices/NIFTY-AUTO",
  },
  {
    label: "Contact Us",
    desc: "Get in touch with our team",
    path: "/contact",
  },
];

export default function NotFound() {
  const navigate = useNavigate();

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#f8fafa", display: "flex", flexDirection: "column" }}>
      <Helmet>
        <title>Page Not Found — ETF India</title>
        <meta name="robots" content="noindex, follow" />
      </Helmet>
      <SectionNavbar header />

      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", px: 3, py: 8, textAlign: "center" }}>

        {/* Illustration */}
        <NotFoundIllustration />

        {/* 404 badge */}
        <Typography
          sx={{ fontSize: "13px", fontWeight: 700, color: "#17332F", letterSpacing: "0.12em", textTransform: "uppercase", mt: 3, mb: 1, opacity: 0.5 }}
        >
          404 — Page Not Found
        </Typography>

        <Typography sx={{ fontSize: { xs: "1.5rem", md: "1.9rem" }, fontWeight: 700, color: "#17332F", mb: 1.5 }}>
          This page doesn't exist
        </Typography>

        <Typography sx={{ fontSize: "15px", color: "#64748b", maxWidth: 420, lineHeight: 1.8, mb: 5 }}>
          The page you're looking for may have been moved, renamed, or doesn't
          exist. Please visit one of the pages below.
        </Typography>

        <Divider sx={{ width: "100%", maxWidth: 560, borderColor: "#e2e8f0", mb: 4 }} />

        {/* Navigation cards */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, justifyContent: "center", maxWidth: 640, width: "100%" }}>
          {PAGES.map(({ label, desc, path }) => (
            <Box
              key={label}
              onClick={() => navigate(path)}
              sx={{
                flex: "1 1 130px",
                maxWidth: 180,
                border: "1px solid #e2e8f0",
                borderRadius: "12px",
                backgroundColor: "#fff",
                p: 2.5,
                cursor: "pointer",
                textAlign: "center",
                transition: "box-shadow 0.2s, border-color 0.2s",
                "&:hover": {
                  borderColor: "#17332F",
                  boxShadow: "0 4px 16px -4px #17332F22",
                },
              }}
            >
              <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#17332F", mb: 0.5 }}>
                {label}
              </Typography>
              <Typography sx={{ fontSize: "11px", color: "#64748b", lineHeight: 1.5 }}>
                {desc}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <MiniFooter />
    </Box>
  );
}

function NotFoundIllustration() {
  return (
    <svg width="300" height="200" viewBox="0 0 300 200" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Big 404 text background */}
      <text x="50%" y="115" textAnchor="middle" fontSize="110" fontWeight="900"
        fill="#17332F" fillOpacity="0.06" fontFamily="sans-serif">
        404
      </text>
      {/* Magnifying glass circle */}
      <circle cx="138" cy="90" r="48" fill="#E8F0EF" stroke="#17332F" strokeWidth="3" />
      <circle cx="138" cy="90" r="36" fill="#fff" stroke="#17332F" strokeWidth="2" />
      {/* X inside glass */}
      <line x1="122" y1="74" x2="154" y2="106" stroke="#17332F" strokeWidth="4" strokeLinecap="round" />
      <line x1="154" y1="74" x2="122" y2="106" stroke="#17332F" strokeWidth="4" strokeLinecap="round" />
      {/* Magnifying handle */}
      <line x1="176" y1="128" x2="200" y2="155" stroke="#17332F" strokeWidth="6" strokeLinecap="round" />
      {/* Decorative dots */}
      <circle cx="40" cy="55" r="5" fill="#17332F" fillOpacity="0.12" />
      <circle cx="265" cy="65" r="7" fill="#17332F" fillOpacity="0.08" />
      <circle cx="55" cy="155" r="4" fill="#17332F" fillOpacity="0.1" />
      <circle cx="260" cy="148" r="5" fill="#17332F" fillOpacity="0.1" />
      {[90, 108, 126, 144, 162, 180, 198, 216].map((x, i) => (
        <circle key={i} cx={x} cy="185" r="2.5" fill="#17332F" fillOpacity="0.15" />
      ))}
    </svg>
  );
}
